import produce from 'immer';

export type SurveyQuestion = {
  id: string;
  templateType: 'survey_question_and_answers';
  answerType: AnswerType;
  toggleAnswerType: () => void;
  image: {
    altTag: string;
    url: string;
    add: (id: string, imageId: string | null, url: string) => void;
    delete: () => void;
  };
  sound: {
    url: string | undefined;
    add: (url: string, id: string) => void;
    delete: () => void;
  };
  question: {
    update: (html: string) => void;
    text: string;
  };
  addAnswer: () => void;
  answers: Array<{
    id: string;
    associationId: string;
    delete: () => void;
    update: (html: string) => void;
    text: string;
    image: {
      alt: string;
      url: string;
      add: (id: string, imageId: string | null, url: string) => void;
      delete: (id: string) => void;
    };
  }>;
};

export enum AnswerType {
  SingleCorrect = 'single_correct',
  MultiCorrect = 'multi_correct',
}

export default function survey_question_and_answers({
  item,
  facets,
  itemsService,
  setItem,
  distractors,
}): SurveyQuestion {
  const deleteAnswer = (answerId) => {
    setItem((prevItem) =>
      produce(prevItem, (draft) => {
        draft.answers = draft.answers.filter((a) => a.id !== answerId);
      })
    );
    facets.delete({ pathParams: { id: answerId } });
  };

  const updateQuestion = async (html) => {
    setItem((prevItem) =>
      produce(prevItem, (draft) => {
        draft.question.text = String(html);
      })
    );
    await distractors.debouncedUpdate({
      pathParams: { id: facets.data[0].anchor.id },
      data: { data: { attributes: { 'text-html': String(html) } } },
      debounceKey: `survey_question_${facets.data[0].anchor.id}`,
    });
  };

  const updateText = async (associationId, html) => {
    if (item) {
      setItem((prevItem) =>
        produce(prevItem, (draft) => {
          const answerIndex = draft.answers.findIndex((a) => a.associationId === associationId);
          if (answerIndex > -1) {
            draft.answers[answerIndex].text = String(html);
          }
        })
      );
      await distractors.debouncedUpdate({
        pathParams: { id: associationId },
        data: { data: { attributes: { 'text-html': String(html) } } },
        debounceKey: `survey_text_${associationId}`,
      });
    }
  };

  const emptyItem = (tempId) => {
    return {
      id: tempId,
      text: '',
      image: {
        id: undefined,
        altTag: '',
        url: '',
      },
    };
  };

  const addAnswer = async () => {
    const tempId = `placeholder-id-${Date.now()}`;

    const newAnswer = emptyItem(tempId);
    setItem((prevItem) =>
      produce(prevItem, (draft) => {
        draft.answers = [...draft.answers, newAnswer];
      })
    );
    const a = await facets.create({
      queryParams: { include: undefined },
      data: {
        include: 'anchor,anchor.image,anchor.sound,association,association.image,association.sound',
        item_id: Number(item.id),
      },
      pathParams: { id: item.id },
    });
    const newId = a.association.id;
    setItem((prevItem) =>
      produce(prevItem, (draft) => {
        const answer = draft.answers.find((a) => a.id === tempId);
        if (answer) {
          answer.id = newId;
          answer.delete = () => deleteAnswer(newId);
          answer.update = (html) => updateText(newId, html);
          answer.image = { ...answer.image, add: () => {}, delete: () => {} };
        }
      })
    );
  };

  const toggleAnswerType = () => {
    setItem((prevItem) =>
      produce(prevItem, (draft) => {
        const newAnswerType =
          prevItem.answerType === AnswerType.MultiCorrect ? AnswerType.SingleCorrect : AnswerType.MultiCorrect;

        itemsService.update({
          pathParams: { id: item.id },
          data: { data: { attributes: { 'answer-type': newAnswerType } } },
        });

        draft.answerType = newAnswerType;
      })
    );
  };

  const addImage = async (id: string, url: string | undefined, imageId: string | null) => {
    setItem((prev) =>
      produce(prev, (draft) => {
        const answerIndex = draft.answers.findIndex((a) => a.associationId === id);
        if (answerIndex > -1) {
          draft.answers[answerIndex].image.url = url;
        }
      })
    );
    await distractors.update({
      pathParams: { id },
      data: { data: { relationships: { image: { data: { id: imageId } } } } },
    });
  };

  const deleteImage = (id: string) => {
    addImage(id, undefined, null);
  };

  const addSound = (url: string | undefined, id: string | undefined) => {
    distractors.update({
      pathParams: { id: item.facets[0].anchor.id },
      data: {
        data: { relationships: { sound: { data: { id } } } },
      },
    });

    setItem((prev) =>
      produce(prev, (draft) => {
        draft.sound.url = url;
      })
    );
  };

  const deleteSound = () => {
    addSound(undefined, undefined);
  };

  return {
    id: item.id,
    templateType: 'survey_question_and_answers',
    answerType: item.answerType,
    toggleAnswerType,
    question: {
      update: (html) => updateQuestion(html),
      text: item.facets[0]?.anchor.textHtml,
    },
    sound: {
      url: item.facets[0]?.anchor.sound?.id,
      add: addSound,
      delete: deleteSound,
    },
    image: {
      altTag: item.facets[0]?.anchor.image?.altTag || '',
      url: item.facets[0]?.anchor.image?.smallUrl || item.facets[0]?.anchor.image?.url,

      // this returns an error. but it also returns an error on the old version so I don't know if this can have an image or how it should be attached
      add: (url, imageId) => addImage(item.facets[0]?.anchor.id, url, imageId),
      delete: () => addImage(item.facets[0]?.anchor.id, undefined, null),
    },
    addAnswer,
    answers: facets.data.map((answer) => ({
      id: answer.id,
      associationId: answer.association.id,
      delete: () => deleteAnswer(answer.id),
      update: (html) => updateText(answer.association.id, html),
      text: answer.association.textHtml,
      image: {
        id: answer.association.image?.id,
        altTag: answer.association.image?.altTag,
        url: answer.association.image?.smallUrl || answer.association.image?.url,
        add: (url, imageId) => addImage(answer.association.id, url, imageId),
        delete: () => deleteImage(answer.association.id),
      },
    })),
  };
}
